import { useCallback, useEffect } from 'react';
import { useFetchData } from '@/utils/ReqUtil';
import type { AuthRedirectResponse } from '@/types/user/login';
import { authStore } from '@/store/authStore';
import { userStore } from '@/store/user/store';
import { preQuestionnaireStore } from '@/store/preQuestionnaireStore';
import { rptStore } from '@/store/rptStore';
import { useFunnelStore } from '@/store/funnelStore';

interface AuthRedirectParams {
  redirect_to: string;
  [key: string]: string | number | boolean | undefined;
}

export const useAuthRedirect = (
  redirectPath: string,
  additionalParams?: Record<string, string | number | boolean | undefined>,
) => {
  const { clearTokens } = authStore();
  const { clearUserData } = userStore();
  const { clear: clearAnswers } = preQuestionnaireStore();
  const { clearRPT } = rptStore();
  const { clearPreviousPath } = useFunnelStore();

  // Build query parameters, merging additional params with defaults
  const params: AuthRedirectParams = {
    redirect_to: `/${redirectPath}`,
    ...additionalParams,
  };

  const { data: authRedirectData } = useFetchData<AuthRedirectResponse>({
    key: [
      'auth_redirects',
      redirectPath,
      additionalParams ? JSON.stringify(additionalParams) : null,
    ],
    url: '/auth_redirects/bufo',
    params,
    enabled: !!redirectPath,
    gcTime: 0,
    staleTime: 0,
    meta: { persist: false },
  });

  const cleanupAndRedirect = useCallback(
    (redirectTo: string) => {
      clearTokens();
      clearUserData();
      clearPreviousPath();
      clearAnswers();
      clearRPT();

      localStorage.clear();

      window.location.href = redirectTo;
    },
    [clearTokens, clearUserData, clearPreviousPath, clearAnswers, clearRPT],
  );

  useEffect(() => {
    if (!authRedirectData) return;

    const redirectTo = authRedirectData.data.redirect_to;
    cleanupAndRedirect(redirectTo);
  }, [authRedirectData, cleanupAndRedirect]);
};
